import React, { useCallback, useState, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';

import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import Typography from '@material-ui/core/Typography';

import { useDispatch } from 'react-redux';
import { countries } from '../../../constants/countries';
import { useOrg, useUser } from '../../../helpers/customHooks';
import { Organisation } from '../../../interfaces/organisation.interface';
import { updateOrganisation } from '../../../redux/organisation/actions/OrganisationActions';
import Paper from '../../../styles/templates/Paper';
import { ContentRow } from '../../../styles/templates/PageTemplates';
import TextField from '../../../styles/templates/DSTextField';
import { useProfileStyles } from './styles';
import {
  completeFileUpload,
  generateFilePresignedUrl,
  uploadFile,
} from '../../../services/FileUploadService';

const CompanyTab: React.FC = () => {
  const classes = useProfileStyles();
  const dispatch = useDispatch();
  const user = useUser();
  const organisation = useOrg(true);
  const [companyName, setCompanyName] = useState(user?.company || '');
  const [country, setCountry] = useState(user?.country || 'United Kingdom');
  const [companyWebsite, setCompanyWebsite] = useState<string | null>(
    organisation?.website || null,
  );

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      const upload = async (file: File) => {
        if (!user) return;

        try {
          const res = await generateFilePresignedUrl({
            id: `${user.organisationId}`,
            kind: 'orgLogo',
          });
          if (res.parsedBody) {
            await uploadFile(file, res.parsedBody.contentUrl);
            const completeUpload = await completeFileUpload(res.parsedBody.fileId);

            if (completeUpload.parsedBody) {
              const org: Organisation = {
                id: user.organisationId,
                email: organisation?.email || null,
                website: companyWebsite,
                name: companyName,
                logoUrl: {
                  fileId: completeUpload.parsedBody.fileId,
                  contentUrl: completeUpload.parsedBody.contentUrl,
                },
                country,
                partnerId: organisation?.partnerId || null,
              };

              dispatch(updateOrganisation(`${user.organisationId}`, org));
            }
          }
        } catch (error) {
          console.log(error);
        }
      };

      upload(acceptedFiles[0]);
    },
    [user, organisation, dispatch, companyName, country, companyWebsite],
  );
  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: 'image/*',
    multiple: false,
  });

  useEffect(() => {
    setCompanyName(organisation?.name || '');
    setCountry(organisation?.country || 'United Kingdom');
    setCompanyWebsite(organisation?.website || null);
  }, [organisation]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (!user || !organisation) return;
      if (
        organisation.name === companyName &&
        organisation.website === companyWebsite &&
        organisation.country === country
      )
        return;

      const org: Organisation = {
        id: user.organisationId,
        email: organisation?.email || null,
        website: companyWebsite,
        name: companyName,
        logoUrl: organisation.logoUrl,
        country,
        partnerId: organisation?.partnerId || null,
      };

      dispatch(updateOrganisation(`${user.organisationId}`, org));
    }, 1000);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, companyWebsite, companyName, country, dispatch]);

  return (
    <Paper>
      <div className={classes.container}>
        <div>
          <Typography variant="subtitle1">Company</Typography>
          <Typography variant="body2" className={classes.subtitle}>
            These details are visable in the HAT App
          </Typography>
        </div>

        <div className={classes.selectRow}>
          <TextField
            id="company"
            name="company"
            label="Company"
            variant="outlined"
            className="formInput"
            autoComplete="off"
            value={companyName}
            onChange={(e) => setCompanyName(e.target.value)}
            focused={false}
          />

          <FormControl className={classes.select} focused={false}>
            <InputLabel id="country-select-label">Country</InputLabel>
            <Select
              variant="outlined"
              labelId="country-select-label"
              id="country-select"
              label="Country"
              value={country}
              onChange={(e) => setCountry(e.target.value as string)}
              IconComponent={ExpandMoreIcon}
              MenuProps={{
                anchorOrigin: {
                  vertical: 'bottom',
                  horizontal: 'left',
                },
                getContentAnchorEl: null,
              }}
            >
              {countries.map((country) => (
                <MenuItem key={country.value} value={country.value}>
                  {country.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>

        <ContentRow>
          <TextField
            id="website"
            name="website"
            label="Company Website"
            variant="outlined"
            className="formInput"
            autoComplete="off"
            value={companyWebsite || ''}
            onChange={(e) => setCompanyWebsite(e.target.value || null)}
            focused={false}
          />
        </ContentRow>

        <div className={classes.imageWrapper}>
          <label htmlFor="ds-profile-icon">
            <Typography>Company Logo</Typography>
          </label>
          <div
            {...getRootProps({
              className: `${classes.uploadCard} ${organisation?.logoUrl ? 'image' : 'dashed'}`,
            })}
          >
            {organisation?.logoUrl ? (
              <>
                <img src={organisation.logoUrl.contentUrl} alt="Company Logo" />
                <div>
                  <input {...getInputProps()} id="ds-profile-icon" />
                </div>
              </>
            ) : (
              <>
                <i className="material-icons">photo</i>
                <div className="ds-profile-icon-title">
                  Drag & Drop or browse to upload an image
                </div>

                <div>
                  <input {...getInputProps()} id="ds-profile-icon" />
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </Paper>
  );
};

export default CompanyTab;
